import { Features } from 'c1gen/features'
import { Roles } from 'c1gen/roles'
import type { Route } from 'lib/routes'
import { LucideIcon } from 'lucide-react'
import { FC, ReactNode } from 'react'

export const navTransitionDuration = '550ms'
export const navSidebarWidthExpanded = '170px'
export const navSidebarWidthCollapsed = '50px'
export const navSidebarOverlayWidth = '260px'
export const navItemWrapperXPaddingPx = 8
export const navItemWrapperYPaddingPx = 2
export const navItemMinHeightPx = 32

export const getNavItemHighlightWidth = (isCollapsed: boolean): string => {
    const sidebarWidth = isCollapsed ? navSidebarWidthCollapsed : navSidebarWidthExpanded
    const totalPadding = navItemWrapperXPaddingPx * 2
    const widthValue = parseInt(sidebarWidth, 10)
    return `${widthValue - totalPadding}px`
}

/**
 * Props for custom flyout components
 */
export type CustomFlyoutProps = {
    /** Callback to expose the flyout element ref for safe area calculation */
    onRef?: (ref: HTMLDivElement | null) => void
    /** Container label to show when sidebar is collapsed */
    label?: ReactNode
    /** Whether this flyout is persistently shown (not just on hover) */
    isPersistent?: boolean
}

/**
 * Top-level navigation grouping (e.g., "My work", "Access", "Apps")
 * Containers appear as primary items in the sidebar
 */
export type NavContainer = {
    /** Unique identifier for the container */
    id: string
    /** Display label (ReactNode, typically a FormattedMessage) */
    label: ReactNode
    /** Lucide icon component */
    icon: LucideIcon
    /** Modules within this container */
    modules: NavModule[]
    /** Required roles - user must have at least one to see this container */
    roles?: Roles[]
    /** Required feature flags - user must have at least one to see this container */
    features?: Features[]
    /** Visual divider before this container */
    separatorBefore?: boolean
    /** Paths where the flyout should persist (stay open even without hover) */
    persistentPaths?: string[]
    /** Custom flyout component (e.g., AppsSidebarOverlay) - if not provided, uses default ModuleFlyout */
    customFlyout?: FC<CustomFlyoutProps>
    /** Custom indicator component (e.g., SystemManagementWarningIndicator) - shown instead of dot when provided */
    customIndicator?: FC
    /** Default path when clicking the container (used for containers with custom flyouts and no modules) */
    defaultPath?: Route
}

/**
 * Feature/module within a container (e.g., "Requests", "Reviews")
 * Modules appear in flyout menus when a container is hovered
 */
export type NavModule = {
    /** Unique identifier for the module */
    id: string
    /** Display label (ReactNode, typically a FormattedMessage) */
    label: ReactNode
    /** Lucide icon component */
    icon: LucideIcon
    /** Route to navigate to */
    pathname: Route
    /** Required roles - user must have at least one */
    roles?: Roles[]
    /** Required feature flags - at least one must be enabled */
    features?: Features[]
    /** Hide if any of these feature flags are enabled */
    hideOnFeatures?: Features[]
    /** Notification indicator count (injected at runtime from DashboardContext) */
    count?: number
    /** Module is coming soon / not yet available */
    placeholder?: boolean
    /** Visual divider before this module */
    separatorBefore?: boolean
    /** Additional paths that should highlight this module as active */
    selectedPaths?: string[]
    /** Custom indicator component (e.g., SystemManagementWarningIndicator) - shown instead of dot when provided */
    customIndicator?: FC
}

/**
 * Settings navigation group (e.g., "Tenant", "Security", "Developers")
 * Settings use a different grouping structure
 */
export type SettingsGroup = {
    /** Unique identifier for the group */
    id: string
    /** Display label (ReactNode, typically a FormattedMessage) */
    label: ReactNode
    /** Settings items within this group */
    items: NavModule[]
}
